import { Button, css, Inline, Paragraph, styled } from 'canoe';
import sum from 'lodash/sum';
import { nanoid } from 'nanoid';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { MultiseriesApi } from '@candu-ai/analytics/content-events';
import {
  Filter,
  FilterOperations,
  FilterTypes,
  setFilters as setFiltersAction,
  useFilters,
} from '@candu-ai/analytics/search';
import { AnalyticQuery } from '@candu-ai/analytics/types';
import { getSeriesColor } from '@candu-ai/analytics/ui';
import { EntitlementName, useEntitlementAccess } from '@candu-ai/organizations/entitlements';

import { aggregateSeries } from './aggregateSeries';

const StyledNumber = styled(Paragraph)<any>`
  ${({ theme, color }) => css`
    margin: 0;
    font-weight: bold;
    line-height: 1;
    color: ${theme.colors[color]};
  `}
`;

const StyledText = styled(Paragraph)`
  margin: 0;
  font-weight: bold;
  line-height: 1;
`;

export const DataSummary = styled.div<{ display: 'row' | 'column' }>`
  ${({ theme, display }) => css`
    padding: 0 ${theme.spaces.normal};

    ${display === 'column' &&
    css`
      margin-bottom: ${theme.spaces.normal};

      & > div {
        flex: 1;
        display: flex;
        flex-direction: column;

        align-items: flex-end;
        justify-content: flex-end;
      }
    `}

    ${display === 'row' &&
    css`
      & > div:not(:first-child) {
        margin-top: ${theme.spaces.s};
      }
    `}
  `}
`;

const FiltersWrapper = styled.div`
  ${({ theme }) => css`
    position: relative;
    margin-right: ${theme.spaces.xxs};

    > div {
      width: 160px;
      height: 24px;
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: space-between;
      right: 0;
      transform: translateY(-50%);
      gap: ${theme.spaces.xs};
    }
  `}
`;

const SummaryRow = styled.div<{ withFilters: boolean }>`
  ${({ withFilters }) =>
    withFilters
      ? css`
          &:not(:hover) {
            ${FiltersWrapper} {
              display: none;
            }
          }
        `
      : ''}
`;

interface SummaryListProps<T> {
  rawData: AnalyticQuery<T>;
  display?: 'row' | 'column';
  multiseriesApi?: MultiseriesApi;
  unit: undefined | '%';
  title: string;
  seriesName?: string;
  withTotalPrefix?: boolean;
}

const ColumnSummaryList = <T extends Record<string, unknown>>({
  rawData,
  unit,
  title,
}: SummaryListProps<T>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [xAxis, yAxis] = rawData.fields;

  const sumData: number = useMemo(() => {
    if (!rawData?.metadata) {
      return 0;
    }
    // we need to grab the xAxis for metric (rather than timeseries) to work
    return sum(rawData.rows.map((r) => r[yAxis?.name || xAxis?.name] as number));
  }, [rawData]);

  return (
    <DataSummary display="column">
      <div>
        <Inline align="center" justify="center" space="s">
          <StyledText size="short" color="OnyxGray">
            {title}
          </StyledText>
          <StyledNumber size="short" color={getSeriesColor(0)}>
            {unit === '%' ? `${(sumData ?? 0).toFixed(1)}%` : sumData?.toLocaleString('en-GB')}
          </StyledNumber>
        </Inline>
      </div>
    </DataSummary>
  );
};

interface FilterActionProps {
  filterType: FilterTypes;
  operand: string;
}

const RowFilterActions = ({ filterType, operand }: FilterActionProps) => {
  const dispatch = useDispatch();
  const filters = useFilters();
  const setFilter = (newFilter: Filter) => dispatch(setFiltersAction([...filters, newFilter]));

  return (
    <FiltersWrapper>
      <div>
        <Button
          variant="secondary"
          size="small"
          onClick={() =>
            setFilter({
              id: nanoid(6),
              type: filterType,
              operation: {
                type: FilterOperations.Equals,
                operand,
              },
            })
          }
        >
          Filter
        </Button>
        <Button
          variant="secondary"
          size="small"
          onClick={() =>
            setFilter({
              id: nanoid(6),
              type: filterType,
              operation: {
                type: FilterOperations.EqualsNot,
                operand,
              },
            })
          }
        >
          Exclude
        </Button>
      </div>
    </FiltersWrapper>
  );
};

const RowSummaryList = <T extends Record<string, unknown>>({
  rawData,
  unit,
  multiseriesApi,
  title,
  seriesName,
}: SummaryListProps<T>) => {
  const { hasEntitlementAccess: hasAnalyticsFiltersAccess } = useEntitlementAccess(
    EntitlementName.AnalyticsFilters,
  );

  const series = multiseriesApi?.getSeriesIds() || [];
  const entries = multiseriesApi?.getEntries() || [];

  const sumData: Record<string, number> = useMemo(() => {
    if (!rawData?.metadata) {
      return Object.fromEntries(series.map((s) => [s, 0]));
    }
    return aggregateSeries(rawData);
  }, [rawData]);

  // don't show anything if there are no rows...
  if (rawData.rows.length === 0) {
    return null;
  }

  return (
    <DataSummary display="row">
      <Inline ml="s" align="center" justify="space-between" space="none">
        <StyledText size="short" color="OnyxGray">
          {seriesName}
        </StyledText>
        <StyledText size="short" color="OnyxGray">
          {title}
        </StyledText>
      </Inline>
      {entries.map(({ value, label, filterType, filterValue }, idx) => (
        <SummaryRow key={label} withFilters={hasAnalyticsFiltersAccess && !!filterType}>
          <Inline ml="s" key={value} align="center" justify="space-between" space="none">
            <span>{label}</span>
            <Inline align="center" space="s">
              {hasAnalyticsFiltersAccess && !!filterType && (
                <RowFilterActions filterType={filterType} operand={filterValue || value} />
              )}
              <StyledNumber size="short" color={getSeriesColor(idx)}>
                {unit === '%'
                  ? `${(sumData[value] ?? 0).toFixed(1)}%`
                  : (sumData[value] ?? 0).toLocaleString('en-GB')}
              </StyledNumber>
            </Inline>
          </Inline>
        </SummaryRow>
      ))}
      {multiseriesApi?.canShowMoreSeries() && (
        <Inline align="center" justify="space-around" space="none">
          <Button
            variant="primary"
            appearance="outline"
            onClick={() => multiseriesApi?.onShowAllSeries()}
          >
            Load more
          </Button>
        </Inline>
      )}
    </DataSummary>
  );
};

export const ContentSummaryList = <T extends Record<string, unknown>>({
  rawData,
  multiseriesApi,
  unit,
  title,
  seriesName,
  withTotalPrefix,
}: SummaryListProps<T>) => {
  const display = rawData.fields.length < 3 && !multiseriesApi ? 'column' : 'row';
  if (display === 'column') {
    return (
      <ColumnSummaryList
        rawData={rawData}
        unit={unit}
        title={withTotalPrefix ? `Total ${title}` : title}
      />
    );
  }
  return (
    <RowSummaryList
      rawData={rawData}
      unit={unit}
      multiseriesApi={multiseriesApi}
      title={title}
      seriesName={seriesName}
    />
  );
};
